/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Which expression shapes declare a default** — one rule, called by everything that asks.
 *
 * It is a file of its own because the question is asked on **both sides of the mirror**, and the
 * two spellings had already begun to drift: `#1269` was this rule's writer (the control that
 * authors a default) and one of its readers (the row's mark) disagreeing, and the fix there named
 * the condition on one side only. `XS5` then needed the same answer for a **third** reason — a
 * reference's local override lands on the referenced expression's own `default` slot, so a shape
 * with no slot has nothing to override — which would have made it three.
 *
 * > **When a rule is written as a condition in more than one place, give it a name and let both
 * > call it.** The name is what makes a disagreement impossible rather than merely unlikely.
 *
 * ## Why these two and not the others
 *
 * A default answers *what if this resolves to nothing*, so a shape that can resolve to nothing is
 * the shape that needs one:
 *
 * | | |
 * | --- | --- |
 * | `calculated` | a path can be absent, and a regex can fail to match |
 * | `computed` | its operands can be |
 * | `macro` | the session may not carry it (`X58`) |
 * | `static` | **it is its own answer.** A literal cannot fail to resolve |
 * | `conditional` | both branches always produce something, or one of them suppresses — so there is nothing left over to default. It may still **transport** one on the branch holding a path (`#1241`), which is a carry rather than a slot of its own |
 * | `suppress` | the outcome is *this does not go out*, which has no value to fall back to |
 * | `reference` | its `default` is the **override** of the referenced expression's, not a slot of its own — see `ReferenceExpression` |
 *
 * ## It takes the discriminant, not the expression
 *
 * So a caller can ask about a shape it has not narrowed, and — the reason it matters here — so this
 * file imports **nothing**. It is aliased into the console under the closed-subgraph rule, and a
 * file that imported `MacroExpression` would drag `PodDto` and `DemandSourceDto` in with it.
 */

/** The shapes with a `default` of their own. Exported so a spec can enumerate rather than restate. */
export const EXPRESSION_SHAPES_DECLARING_A_DEFAULT: readonly string[] = Object.freeze([
  'calculated',
  'computed',
  'macro',
]);

/** Does an expression of this shape declare a default of its own? */
export function declaresDefaultSlot(type: string | undefined): boolean {
  return type !== undefined && EXPRESSION_SHAPES_DECLARING_A_DEFAULT.includes(type);
}
